import { useQuery } from '@tanstack/react-query'
import { CalendarDays, Clock, MapPin, Users } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PageHero, SectionTitle } from '../components/Common'
import { api } from '../services/api'
import type { EventItem } from '../types/content'

const eventTypes = [
  { label: 'Tất cả', value: 'all' },
  { label: 'Lịch công tác', value: 'work_schedule' },
  { label: 'Sự kiện trường', value: 'school_event' },
  { label: 'Thi/kiểm tra', value: 'exam' },
  { label: 'Cuộc họp', value: 'meeting' },
]

const eventTypeLabels: Record<string, string> = {
  work_schedule: 'Lịch công tác',
  school_event: 'Sự kiện trường',
  exam: 'Thi/kiểm tra',
  meeting: 'Cuộc họp',
}

function formatDate(value?: string) {
  if (!value) return ''
  return new Date(value).toLocaleDateString('vi-VN', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatTime(value?: string, allDay?: boolean) {
  if (allDay) return 'Cả ngày'
  if (!value) return ''
  return new Date(value).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
}

function richTextToParagraphs(content: unknown): string[] {
  const root = content && typeof content === 'object' && 'root' in content ? (content as any).root : undefined
  const children: any[] = Array.isArray(root?.children) ? root.children : []

  return children
    .map((node: any) => {
      const textNodes = Array.isArray(node?.children) ? node.children : []
      return textNodes.map((child: any) => child?.text).filter(Boolean).join('')
    })
    .filter((paragraph): paragraph is string => Boolean(paragraph))
}

function EventMeta({ item }: { item: EventItem }) {
  return (
    <div className="event-meta">
      <span><CalendarDays />{formatDate(item.startAt)}</span>
      <span><Clock />{formatTime(item.startAt, item.allDay)}{item.endAt && !item.allDay ? ` - ${formatTime(item.endAt)}` : ''}</span>
      {item.location && <span><MapPin />{item.location}</span>}
      {item.organizer && <span><Users />{item.organizer}</span>}
    </div>
  )
}

export function ScheduleListPage() {
  const [selected, setSelected] = useState('all')
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['events', selected, page],
    queryFn: () => api.getEvents(page, 20, selected),
  })

  const items = data?.data ?? []
  const totalPages = data?.meta?.totalPages ?? 1

  return (
    <>
      <PageHero title="LỊCH CÔNG TÁC" />
      <div className="container generic-page schedule-page">
        <SectionTitle>LỊCH CÔNG TÁC - SỰ KIỆN</SectionTitle>
        <div className="category-tabs">
          {eventTypes.map((type) => (
            <button
              key={type.value}
              className={selected === type.value ? 'active' : ''}
              onClick={() => { setSelected(type.value); setPage(1) }}
            >
              {type.label}
            </button>
          ))}
        </div>

        {isLoading && <p>Đang tải lịch công tác...</p>}
        {isError && <p>Không thể tải lịch công tác.</p>}
        {!isLoading && !isError && items.length === 0 && <p>Chưa có lịch công tác nào.</p>}

        <div className="schedule-list">
          {items.map((item) => (
            <article key={item.id}>
              <time dateTime={item.startAt}>
                <strong>{new Date(item.startAt).toLocaleDateString('vi-VN', { day: '2-digit' })}</strong>
                <span>Tháng {new Date(item.startAt).toLocaleDateString('vi-VN', { month: '2-digit' })}</span>
              </time>
              <div>
                <span className="badge">{eventTypeLabels[item.eventType ?? ''] ?? 'Lịch công tác'}</span>
                <h2><Link to={`/lich-cong-tac/${item.slug}`}>{item.title}</Link></h2>
                <EventMeta item={item} />
                <p>{item.summary || 'Thông tin lịch công tác được nhà trường cập nhật công khai.'}</p>
              </div>
              <Link className="schedule-detail-link" to={`/lich-cong-tac/${item.slug}`}>Xem chi tiết</Link>
            </article>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export function ScheduleDetailPage() {
  const { slug } = useParams()
  const { data: item, isLoading, isError } = useQuery({
    queryKey: ['event', slug],
    queryFn: () => api.getEvent(slug || ''),
    enabled: Boolean(slug),
  })

  if (isLoading) return (
    <>
      <PageHero title="LỊCH CÔNG TÁC" />
      <div className="container generic-page"><p>Đang tải chi tiết lịch công tác...</p></div>
    </>
  )

  if (isError || !item) return (
    <>
      <PageHero title="LỊCH CÔNG TÁC" />
      <div className="container generic-page"><p>Không tìm thấy lịch công tác.</p></div>
    </>
  )

  const paragraphs = richTextToParagraphs(item.content)

  return (
    <>
      <div className="container breadcrumb-line">
        <Link to="/">Trang chủ</Link><span>›</span>
        <Link to="/lich-cong-tac">Lịch công tác</Link><span>›</span>
        <span>Chi tiết</span>
      </div>
      <article className="container schedule-detail-page">
        <span className="badge">{eventTypeLabels[item.eventType ?? ''] ?? 'Lịch công tác'}</span>
        <h1>{item.title}</h1>
        <EventMeta item={item} />
        {item.summary && <p className="lead">{item.summary}</p>}
        <div className="article-body">
          {paragraphs.length > 0
            ? paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)
            : <p>Chi tiết lịch công tác đang được cập nhật.</p>
          }
        </div>
        <Link className="back-link" to="/lich-cong-tac">← Quay lại danh sách lịch công tác</Link>
      </article>
    </>
  )
}
